import React, { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import classNames from 'classnames'

import { TooltipWrapper } from 'components/Tooltip/Tooltip'

import { getDate, FORMATS } from 'utils/dateTime'

import { IconCrown, IconFlag, IconTimer, IconHelp } from 'svg'

import PulseDot from './components/PulseDot/PulseDot'

import styles from './Indicator.module.scss'

const PERIOD_TYPE = {
    SCHEDULED: 'SCHEDULED',
    ENDED: 'ENDED',
}

const Indicator = ({ project, isMultiplayer, isPublished, isDraft, isTemplate, isUpgrade }) => {
    const { t } = useTranslation()

    const [tooltipText, setTooltipText] = useState(null)

    const [periodType, setPeriodType] = useState(null)

    useEffect(() => {
        if (project.limitActivePeriod) {
            if (project.endDate <= Date.now()) {
                setPeriodType(PERIOD_TYPE.ENDED)
                return
            }
            if (project.startDate >= Date.now()) {
                setPeriodType(PERIOD_TYPE.SCHEDULED)
                return
            }
        }
        setPeriodType(null)
    }, [project])

    useEffect(() => {
        if (isUpgrade) {
            setTooltipText(
                <div className={styles.upgradeTooltip}>
                    <p>
                        {t('Special plan')}:{' '}
                        <a
                            href="https://help.interacty.me/content/can-i-purchase-a-subscription-just-for-one-project"
                            target="_blank"
                        >
                            {t(project.projectSubscription.productName)}
                        </a>
                    </p>
                    <p>
                        {t('Active until')}: <span>{getDate(project.projectSubscription.expirationDate)}</span>
                    </p>
                </div>,
            )
            return
        }
        if (periodType === PERIOD_TYPE.SCHEDULED) {
            setTooltipText(
                <Trans
                    i18nKey="Users see a Coming Soon page. The project is scheduled to start on {{startDate}}. <lnk>Learn more</lnk>"
                    values={{
                        startDate: getDate(project.startDate, FORMATS.shortDate2),
                    }}
                    components={{
                        lnk: (
                            <a href="https://help.interacty.me/en/content/how-to-set-a-start-and-end-date-for-your-project-active-period" />
                        ),
                    }}
                />,
            )
            return
        }
        if (periodType === PERIOD_TYPE.ENDED) {
            setTooltipText(
                <Trans
                    i18nKey="To reactivate, change the dates and republish the project. <lnk>Learn more</lnk>"
                    components={{
                        lnk: (
                            <a href="https://help.interacty.me/en/content/how-to-set-a-start-and-end-date-for-your-project-active-period" />
                        ),
                    }}
                />,
            )
            return
        }

        setTooltipText(null)
    }, [project, isUpgrade, periodType])

    return (
        <TooltipWrapper tooltipText={tooltipText ? <div className={styles.tooltip}>{tooltipText}</div> : null}>
            <div
                className={classNames(styles.indicator, {
                    [styles.isHasTooltip]: !!tooltipText,
                    [styles.isMultiplayer]: isMultiplayer,
                    [styles.isPublished]: isPublished,
                    [styles.isScheduled]: periodType === PERIOD_TYPE.SCHEDULED,
                    [styles.isEnded]: periodType === PERIOD_TYPE.ENDED,
                    [styles.isDraft]: isDraft,
                    [styles.isTemplate]: isTemplate,
                    [styles.isUpgrade]: isUpgrade,
                })}
            >
                <div className={styles.content}>
                    {isPublished && (
                        <>
                            {periodType === PERIOD_TYPE.SCHEDULED && (
                                <>
                                    <IconTimer /> {t('Scheduled')} <IconHelp />
                                </>
                            )}
                            {periodType === PERIOD_TYPE.ENDED && (
                                <>
                                    <IconFlag /> {t('Ended')} <IconHelp />
                                </>
                            )}
                            {!periodType && (
                                <>
                                    <PulseDot /> {t('Live')}
                                </>
                            )}
                        </>
                    )}
                    {isDraft && t('Draft')}
                    {isTemplate && t('Template')}
                    {isMultiplayer && t('Game event')}
                    {isUpgrade && (
                        <>
                            <IconCrown color="#fff" />
                            <p>{t(project.projectSubscription.productName)}</p>
                        </>
                    )}
                </div>
            </div>
        </TooltipWrapper>
    )
}

export default Indicator
